/**
 * Cliente do gateway WebSocket.
 *
 * Reconecta sozinho com backoff exponencial e jitter. O jitter importa: sem
 * ele, uma queda do servidor faria todos os clientes voltarem no mesmo
 * milissegundo e derrubarem o servidor de novo assim que ele subisse.
 */

import { backoffDelay, type ClientEvent, type ServerEvent } from '@nexus/shared'
import { tokens } from './api'
import { clientKind } from './desktop'

type EventHandler = (event: ServerEvent) => void
type StatusHandler = (status: ConnectionStatus) => void

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed'

const WS_URL = (() => {
  const configured = import.meta.env.VITE_GATEWAY_URL
  if (configured) return configured
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${location.host}/gateway`
})()

class GatewayClient {
  private socket: WebSocket | null = null
  private handlers = new Set<EventHandler>()
  private statusHandlers = new Set<StatusHandler>()

  private status: ConnectionStatus = 'idle'
  private attempt = 0
  private heartbeatTimer: number | null = null
  private reconnectTimer: number | null = null
  private lastSequence = 0
  private sessionId: string | null = null
  private intentionalClose = false

  /** Eventos que não puderam sair enquanto a conexão estava fora do ar. */
  private pendentes: ClientEvent[] = []

  /** Latência do último heartbeat, exibida na barra de status. */
  latencyMs = 0
  private heartbeatSentAt = 0
  /** A primeira medida ainda não subiu ao servidor. */
  private latenciaPendente = false

  connect(): void {
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) {
      return
    }
    if (!tokens.access) return

    this.intentionalClose = false
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting')

    const socket = new WebSocket(WS_URL)
    this.socket = socket

    socket.onopen = () => {
      // O servidor manda `hello` primeiro; o identify sai na resposta a ele.
    }

    socket.onmessage = (raw) => {
      let event: ServerEvent
      try {
        event = JSON.parse(raw.data as string) as ServerEvent
      } catch {
        return
      }
      this.handleEvent(event)
    }

    socket.onclose = (event) => {
      this.stopHeartbeat()
      this.socket = null

      if (this.intentionalClose) {
        this.setStatus('closed')
        return
      }

      /*
        Encerrar de vez só faz sentido quando a credencial é o problema.

        O servidor usa o mesmo código para "a primeira mensagem não foi o
        identify", e nesse caso a sessão continua válida: reconectar resolve.
        Tratar os dois como token inválido deixava o cliente mudo até alguém
        recarregar a página, com a tela avisando que não havia conexão enquanto o
        resto parecia funcionar.
      */
      if (event.reason === 'identify_required') {
        this.scheduleReconnect()
        return
      }

      if (event.code === 4001 || event.code === 4007) {
        this.setStatus('closed')
        this.handlers.forEach((fn) =>
          fn({ op: 'invalid_session', d: { reason: 'invalid_token', resumable: false } }),
        )
        return
      }

      this.scheduleReconnect()
    }

    socket.onerror = () => {
      // O evento de erro não traz detalhe útil no navegador; o onclose que vem
      // em seguida é quem decide o que fazer.
    }
  }

  private handleEvent(event: ServerEvent): void {
    switch (event.op) {
      case 'hello': {
        this.send({
          op: 'identify',
          d: {
            token: tokens.access ?? '',
            client: clientKind,
            ...(this.sessionId && this.lastSequence > 0
              ? { resume: { sessionId: this.sessionId, lastSequence: this.lastSequence } }
              : {}),
          },
        })
        this.startHeartbeat(event.d.heartbeatIntervalMs)
        return
      }

      case 'ready': {
        this.sessionId = event.d.sessionId
        this.lastSequence = event.s
        this.attempt = 0
        this.setStatus('connected')
        this.descarregarPendentes()

        /*
          Um heartbeat logo de saída, sem esperar o intervalo.

          A medida da latência só existe depois de uma ida e volta completa, e
          ela sobe no pacote seguinte. Esperando o ritmo normal, o painel ficaria
          um minuto inteiro sem saber a latência de quem acabou de entrar.
        */
        this.latenciaPendente = true
        this.heartbeatSentAt = Date.now()
        this.send({ op: 'heartbeat', d: { sequence: this.lastSequence } })
        break
      }

      case 'heartbeat_ack': {
        this.latencyMs = Date.now() - this.heartbeatSentAt

        // A primeira medida vale um pacote extra: é ela que tira a conexão do
        // estado "latência desconhecida" no painel.
        if (this.latenciaPendente) {
          this.latenciaPendente = false
          this.send({
            op: 'heartbeat',
            d: { sequence: this.lastSequence, latencyMs: this.latencyMs },
          })
        }
        return
      }

      default:
        break
    }

    if ('s' in event && typeof event.s === 'number') {
      this.lastSequence = event.s
    }

    this.handlers.forEach((fn) => fn(event))
  }

  private startHeartbeat(intervalMs: number): void {
    this.stopHeartbeat()
    this.heartbeatTimer = window.setInterval(() => {
      // A latência que sobe é a da viagem anterior: a atual só se conhece
      // quando a resposta chega, e aí o pacote já saiu.
      const medida = this.latencyMs > 0 ? this.latencyMs : undefined
      this.heartbeatSentAt = Date.now()
      this.send({ op: 'heartbeat', d: { sequence: this.lastSequence, latencyMs: medida } })
    }, intervalMs)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return

    const delay = backoffDelay(this.attempt)
    this.attempt++
    this.setStatus('reconnecting')

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  /**
   * Envia um evento, guardando o que não puder sair agora.
   *
   * Antes, qualquer mensagem enviada com o socket fechado sumia sem deixar
   * rastro. Entrar num canal de voz durante uma reconexão, por exemplo, perdia o
   * aviso de presença: a chamada funcionava pelo SFU e a pessoa não aparecia na
   * lista para ninguém. Agora a mensagem espera a conexão voltar.
   *
   * O `identify` nunca entra na fila: ele é a resposta ao `hello` e só faz
   * sentido no socket que o recebeu.
   */
  send(event: ClientEvent): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(event))
      return
    }
    if (event.op === 'identify' || event.op === 'heartbeat') return

    // Um teto evita que uma queda longa acumule mensagens sem limite.
    if (this.pendentes.length >= 32) this.pendentes.shift()
    this.pendentes.push(event)
  }

  /** Solta o que ficou esperando, na ordem em que foi pedido. */
  private descarregarPendentes(): void {
    if (this.pendentes.length === 0) return
    const fila = this.pendentes
    this.pendentes = []
    for (const evento of fila) this.send(evento)
  }

  disconnect(): void {
    this.intentionalClose = true
    this.stopHeartbeat()
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.sessionId = null
    this.lastSequence = 0
    this.attempt = 0
    this.socket?.close(1000, 'client_disconnect')
    this.socket = null
    this.setStatus('idle')
  }

  /** Força uma reconexão imediata, usada ao voltar de segundo plano. */
  reconnectNow(): void {
    if (this.status === 'connected') return
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.attempt = 0
    this.connect()
  }

  onEvent(handler: EventHandler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler)
    handler(this.status)
    return () => this.statusHandlers.delete(handler)
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return
    this.status = status
    this.statusHandlers.forEach((fn) => fn(status))
  }

  get currentStatus(): ConnectionStatus {
    return this.status
  }
}

export const gateway = new GatewayClient()

/**
 * O navegador congela timers de abas em segundo plano, então o heartbeat para e
 * o servidor derruba a sessão. Ao voltar, forçamos a reconexão em vez de
 * esperar o próximo passo do backoff.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') gateway.reconnectNow()
  })
  window.addEventListener('online', () => gateway.reconnectNow())
}
